import ParticleSystem, {
  Alpha,
  Body,
  Color,
  Emitter,
  Life,
  Mass,
  RadialVelocity,
  Attraction,
  Force,
  Spring,
  RandomDrift,
  Rate,
  Scale,
  Span,
  SpriteRenderer,
  GPURenderer,
  Vector3D,
} from "three-nebula";
import * as THREE from "three";

import {Object3D} from "three";
import particlePresets from "utils/particlePresets";

class CustomParticleSystem extends Object3D {
  constructor(textureImg) {
    super();
    this.name = "ParticleSystem";
    this.system = new ParticleSystem(0);
    this.renderer = new SpriteRenderer(this, THREE);
    this.emitters = [];
    this.emitterId = null;
    this.canCollide = false;
    this.userData = {
      type: "particleSystem",
      system: particlePresets("default", textureImg),

      // parameters for edit mode emitters
      isAttractionActive: true,
      isForceActive: true,
      isRandomDriftActive: true,
      isSpringActive: true,
      particlesMin: 5,
      particlesMax: 7,
      perSecondMin: 0.1,
      perSecondMax: 0.2,
      image: null,
      massMin: 1,
      massMax: 1,
      lifeMin: 1,
      lifeMax: 1.5,
      radialSpeed: 50,
      radialX: 0,
      radialY: 1,
      radialZ: 0,
      radialTheta: 30,
      alphaA: 1,
      alphaB: 0,
      scaleA: 0.4,
      scaleB: 0.2,
      colorA: "#ffff00",
      colorB: "#ffff11",
      attractionX: 0,
      attractionY: 0,
      attractionZ: 0,
      attractionForce: 0,
      forceX: 0,
      forceY: 0,
      forceZ: 0,
      randomDriftX: 0,
      randomDriftY: 0,
      randomDriftZ: 0,
      springX: 0,
      springY: 0,
      springZ: 0,
      springFactor: 0,
      springFriction: 0,

      // initializers and hehaviours
      texture: new Body(this.createTexture(this.userData.image)),
      mass: new Mass(this.userData.massMin, this.userData.massMax),
      life: new Life(this.userData.lifeMin, this.userData.lifeMax),
      rate: new Rate(
        new Span(this.userData.particlesMin, this.userData.particlesMax),
        new Span(this.userData.perSecondMin, this.userData.perSecondMax),
      ),
      radialVelocity: new RadialVelocity(
        this.userData.radialSpeed,
        new Vector3D(this.userData.radialX, this.userData.radialY, this.userData.radialZ),
        this.userData.radialTheta,
      ),
      particleAlpha: new Alpha(this.userData.alphaA, this.userData.alphaB),
      particleScale: new Scale(this.userData.scaleA, this.userData.scaleB),
      particleColor: new Color(this.userData.colorA, this.userData.colorB),
      force: new Force(this.userData.forceX, this.userData.forceY, this.userData.forceZ),
      randomDrift: new RandomDrift(this.userData.randomDriftX, this.userData.randomDriftY, this.userData.randomDriftZ),
      spring: new Spring(
        this.userData.springX,
        this.userData.springY,
        this.userData.springZ,
        this.userData.springFactor,
        this.userData.springFriction,
      ),
      attraction: new Attraction(
        new Vector3D(this.userData.attractionX, this.userData.attractionY, this.userData.attractionZ),
        this.userData.attractionForce,
      ),
    };
  }

  setPreset(name) {
    this.userData.system = particlePresets(name);
    if (
      this.userData.system.emitters[0].behaviours.find(behavior => {
        return behavior.type === "Attraction";
      })
    ) {
      this.userData.isAttractionActive = true;
    } else {
      this.userData.isAttractionActive = false;
    }
    if (
      this.userData.system.emitters[0].behaviours.find(behavior => {
        return behavior.type === "Force";
      })
    ) {
      this.userData.isForceActive = true;
    } else {
      this.userData.isForceActive = false;
    }
    if (
      this.userData.system.emitters[0].behaviours.find(behavior => {
        return behavior.type === "RandomDrift";
      })
    ) {
      this.userData.isRandomDriftActive = true;
    } else {
      this.userData.isRandomDriftActive = false;
    }
    if (
      this.userData.system.emitters[0].behaviours.find(behavior => {
        return behavior.type === "Spring";
      })
    ) {
      this.userData.isSpringActive = true;
    } else {
      this.userData.isSpringActive = false;
    }
  }

  updateProperties() {
    this.userData.texture = new Body(this.createTexture(this.userData.image));
    this.userData.mass = new Mass(this.userData.massMin, this.userData.massMax);
    this.userData.life = new Life(this.userData.lifeMin, this.userData.lifeMax);
    this.userData.rate = new Rate(
      new Span(this.userData.particlesMin, this.userData.particlesMax),
      new Span(this.userData.perSecondMin, this.userData.perSecondMax),
    );
    this.userData.radialVelocity = new RadialVelocity(
      this.userData.radialSpeed,
      new Vector3D(this.userData.radialX, this.userData.radialY, this.userData.radialZ),
      this.userData.radialTheta,
    );

    this.userData.particleAlpha = new Alpha(this.userData.alphaA, this.userData.alphaB);
    this.userData.particleScale = new Scale(this.userData.scaleA, this.userData.scaleB);
    this.userData.particleColor = new Color(this.userData.colorA, this.userData.colorB);
    this.userData.force = new Force(this.userData.forceX, this.userData.forceY, this.userData.forceZ);
    this.userData.randomDrift = new RandomDrift(this.userData.randomDriftX, this.userData.randomDriftY, this.userData.randomDriftZ);
    this.userData.spring = new Spring(
      this.userData.springX,
      this.userData.springY,
      this.userData.springZ,
      this.userData.springFactor,
      this.userData.springFriction,
    );
    this.userData.attraction = new Attraction(
      new Vector3D(this.userData.attractionX, this.userData.attractionY, this.userData.attractionZ),
      this.userData.attractionForce,
    );
  }

  updateParametersFromUserData(index, userData = null) {
    let emitter;

    if (userData !== null) {
      emitter = userData.system.emitters[index];
      this.userData.isAttractionActive = userData.isAttractionActive;
      this.userData.isForceActive = userData.isForceActive;
      this.userData.isRandomDriftActive = userData.isRandomDriftActive;
      this.userData.isSpringActive = userData.isSpringActive;
    } else {
      emitter = this.userData.system.emitters[index];
    }
    if (emitter !== undefined) {
      const attraction = emitter.behaviours.find(behavior => {
        return behavior.type === "Attraction";
      });
      const force = emitter.behaviours.find(behavior => {
        return behavior.type === "Force";
      });
      const randomDrift = emitter.behaviours.find(behavior => {
        return behavior.type === "RandomDrift";
      });
      const spring = emitter.behaviours.find(behavior => {
        return behavior.type === "Spring";
      });
      this.userData.particlesMin = emitter.rate.particlesMin;
      this.userData.particlesMax = emitter.rate.particlesMax;
      this.userData.perSecondMin = emitter.rate.perSecondMin;
      this.userData.perSecondMax = emitter.rate.perSecondMin;
      this.userData.image = emitter.initializers.find(initializer => {
        return initializer.type === "BodySprite";
      }).properties.texture;
      this.userData.massMin = emitter.initializers.find(initializer => {
        return initializer.type === "Mass";
      }).properties.min;
      this.userData.massMax = emitter.initializers.find(initializer => {
        return initializer.type === "Mass";
      }).properties.max;
      this.userData.lifeMin = emitter.initializers.find(initializer => {
        return initializer.type === "Life";
      }).properties.min;
      this.userData.lifeMax = emitter.initializers.find(initializer => {
        return initializer.type === "Life";
      }).properties.max;
      this.userData.radialSpeed = emitter.initializers.find(initializer => {
        return initializer.type === "RadialVelocity";
      }).properties.radius;
      this.userData.radialX = emitter.initializers.find(initializer => {
        return initializer.type === "RadialVelocity";
      }).properties.x;
      this.userData.radialY = emitter.initializers.find(initializer => {
        return initializer.type === "RadialVelocity";
      }).properties.y;
      this.userData.radialZ = emitter.initializers.find(initializer => {
        return initializer.type === "RadialVelocity";
      }).properties.z;
      this.userData.radialTheta = emitter.initializers.find(initializer => {
        return initializer.type === "RadialVelocity";
      }).properties.theta;
      this.userData.alphaA = emitter.behaviours.find(behavior => {
        return behavior.type === "Alpha";
      }).properties.alphaA;
      this.userData.alphaB = emitter.behaviours.find(behavior => {
        return behavior.type === "Alpha";
      }).properties.alphaB;
      this.userData.scaleA = emitter.behaviours.find(behavior => {
        return behavior.type === "Scale";
      }).properties.scaleA;
      this.userData.scaleB = emitter.behaviours.find(behavior => {
        return behavior.type === "Scale";
      }).properties.scaleB;
      this.userData.colorA = emitter.behaviours.find(behavior => {
        return behavior.type === "Color";
      }).properties.colorA;
      this.userData.colorB = emitter.behaviours.find(behavior => {
        return behavior.type === "Color";
      }).properties.colorB;

      if (attraction) {
        this.userData.attractionX = attraction.properties.x;
        this.userData.attractionY = attraction.properties.y;
        this.userData.attractionZ = attraction.properties.z;
        this.userData.attractionForce = attraction.properties.force;
      }

      if (force) {
        this.userData.forceX = force.properties.fx;
        this.userData.forceY = force.properties.fy;
        this.userData.forceZ = force.properties.fz;
      }

      if (randomDrift) {
        this.userData.randomDriftX = randomDrift.properties.x;
        this.userData.randomDriftY = randomDrift.properties.y;
        this.userData.randomDriftZ = randomDrift.properties.z;
      }

      if (spring) {
        this.userData.springX = spring.properties.x;
        this.userData.springY = spring.properties.y;
        this.userData.springZ = spring.properties.z;
        this.userData.springFactor = spring.properties.spring;
        this.userData.springFriction = spring.properties.friction;
      }
    }
  }

  createTexture(image) {
    let map = new THREE.TextureLoader().load(image);
    let material = new THREE.SpriteMaterial({
      map: map,
      blending: THREE.AdditiveBlending,
    });
    return new THREE.Sprite(material);
  }

  createEmitter() {
    const emitter = new Emitter();

    this.updateProperties();

    return emitter
      .setRate(this.userData.rate)
      .setInitializers([this.userData.texture, this.userData.life, this.userData.mass, this.userData.radialVelocity])
      .setBehaviours([
        this.userData.particleAlpha,
        this.userData.particleScale,
        this.userData.particleColor,
        this.userData.attraction,
        this.userData.force,
        this.userData.randomDrift,
        this.userData.spring,
      ])
      .emit();
  }

  resetEmitter(index) {
    this.updateProperties();

    let behaviours = [this.userData.particleAlpha, this.userData.particleScale, this.userData.particleColor];

    if (this.userData.isAttractionActive) {
      behaviours.push(this.userData.attraction);
    }
    if (this.userData.isForceActive) {
      behaviours.push(this.userData.force);
    }
    if (this.userData.isRandomDriftActive) {
      behaviours.push(this.userData.randomDrift);
    }
    if (this.userData.isSpringActive) {
      behaviours.push(this.userData.spring);
    }

    this.system.emitters[index]
      .setRate(this.userData.rate)
      .setInitializers([this.userData.texture, this.userData.life, this.userData.mass, this.userData.radialVelocity])
      .setBehaviours(behaviours);
  }

  resetSystemEmitters() {
    while (this.emitters !== undefined) {
      this.system.removeEmitter(this.emitters[0]);
    }
  }
  // Initalizing Particle System, call only once.
  createSystem() {
    const textureImg = this.userData.image;
    this.updateParametersFromUserData(0);
    this.updateProperties(0);
    this.system.addEmitter(this.createEmitter());
    this.system.addRenderer(this.renderer);
    if (textureImg) {
      //temporal condition for old library. need to delete later
      this.userData.system.emitters[0].initializers.find(behavior => {
        return behavior.type === "BodySprite";
      }).properties.texture = textureImg;
    }
  }
}

export default CustomParticleSystem;
